import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { getApiBaseUrl, getSocketIoPath } from '../../core/config/api';
import { logout } from '../../core/store/slices/userSlice';
import { RootState } from '../../core/store/store';

/**
 * Écoute globale (montée une fois dans App.tsx) des events de maintenance temps réel :
 * - `maintenance:warning` : alerte 15/5 min avant une maintenance programmée (joueurs en jeu).
 * - `maintenance:force-disconnect` : déconnexion forcée immédiate (maintenance activée ou déclenchée).
 */
const MaintenanceSocketListener: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);
  const [warning, setWarning] = useState<{ minutesRemaining: number; message: string } | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    if (!token) return;

    const socket: Socket = io(getApiBaseUrl(), {
      path: getSocketIoPath(),
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
    });

    socket.on('maintenance:warning', (data: { minutesRemaining: number; message: string }) => {
      setWarning(data);
    });

    socket.on('maintenance:force-disconnect', (data: { message?: string }) => {
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('auth_token');
      dispatch(logout());
      navigate('/', { replace: true });
      window.alert(data?.message || 'Le serveur passe en maintenance, vous avez été déconnecté.');
    });

    return () => {
      socket.disconnect();
    };
  }, [isAuthenticated, dispatch, navigate]);

  if (!warning) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-900/95 backdrop-blur-md border-b-2 border-amber-500/60">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-3">
        <i className="fas fa-triangle-exclamation text-amber-300 text-xl animate-pulse" />
        <p className="text-amber-100 text-sm font-semibold">
          ⚠️ Maintenance dans {warning.minutesRemaining} minute{warning.minutesRemaining > 1 ? 's' : ''} — {warning.message}
        </p>
      </div>
    </div>
  );
};

export default MaintenanceSocketListener;
